import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '@/services/admin.service';
import { roleService } from '@/services/role.service';
import { Plus, Edit, Trash2, X, Save, Search, Users as UsersIcon, UserCheck, UserX, Mail, Shield, Calendar, Download, Home, ChevronRight, Eye, EyeOff, Key, Copy } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { PermissionSelector } from '@/components/admin/PermissionSelector';
import { ROLE_PERMISSIONS } from '@/config/rolePermissions';
import { useAuthStore } from '@/store/authStore';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '@/utils/deleteConfirmation';

export function UserManagement() {
  const { showToast } = useToast();
  const { user: currentUser } = useAuthStore();
  const userPermissions = currentUser?.permissions || [];
  const canEdit = userPermissions.includes('user_management_edit');
  const canDelete = userPermissions.includes('user_management_delete');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showPassword, setShowPassword] = useState(false);
  const [resetPassword, setResetPassword] = useState<{
    isOpen: boolean;
    userId?: string;
    userName?: string;
    newPassword?: string;
  }>({ isOpen: false });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    customPermissions: {} as Record<string, boolean>,
    organization: '',
    exams: [] as string[],
  });
  const [editFormData, setEditFormData] = useState({
    userId: '',
    role: 'admin',
    customPermissions: {} as Record<string, boolean>,
  });
  const queryClient = useQueryClient();

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('accessToken') || '';
        return await adminService.getAllUsers(token);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        return [];
      }
    },
  });

  // Fetch roles from roles module
  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('accessToken') || '';
        return await roleService.getAllRoles(token, { isActive: true });
      } catch (error) {
        console.error('Failed to fetch roles:', error);
        return [];
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const token = localStorage.getItem('accessToken') || '';
      return await adminService.createUser(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsAddingNew(false);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'admin',
        customPermissions: {},
        organization: '',
        exams: [],
      });
      showToast('success', 'User Created', 'The user has been created successfully.');
    },
    onError: () => {
      showToast('error', 'Creation Failed', 'Failed to create user. Please try again.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { userId: string; role: string; customPermissions: Record<string, boolean> }) => {
      const token = localStorage.getItem('accessToken') || '';
      return await adminService.updateUser(data.userId, {
        role: data.role,
        customPermissions: data.customPermissions
      }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditingId(null);
      setEditFormData({
        userId: '',
        role: 'admin',
        customPermissions: {},
      });
      showToast('success', 'User Updated', 'User permissions have been updated successfully.');
    },
    onError: () => {
      showToast('error', 'Update Failed', 'Failed to update user. Please try again.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('accessToken') || '';
      return await adminService.deleteUser(id, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showDeleteSuccess('The user has been deleted successfully.');
    },
    onError: (error: any) => {
      showDeleteError(error.response?.data?.error || 'Failed to delete user. Please try again.');
    },
  });

  const handleDeleteUser = async (userId: string, userName: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete User?',
      itemName: userName,
    });

    if (confirmed) {
      deleteMutation.mutate(userId);
    }
  };

  const resetPasswordMutation = useMutation({
    mutationFn: async (data: { userId: string; password: string }) => {
      const token = localStorage.getItem('accessToken') || '';
      return await adminService.updateUser(data.userId, {
        password: data.password
      }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast('success', 'Password Reset', 'Password has been reset successfully.');
    },
    onError: () => {
      showToast('error', 'Reset Failed', 'Failed to reset password. Please try again.');
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (data: { userId: string; isActive: boolean }) => {
      const token = localStorage.getItem('accessToken') || '';
      return await adminService.updateUser(data.userId, {
        isActive: data.isActive
      }, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast('success', 'Status Updated', `User ${variables.isActive ? 'activated' : 'deactivated'} successfully.`);
    },
    onError: () => {
      showToast('error', 'Update Failed', 'Failed to update user status. Please try again.');
    },
  });

  const generateRandomPassword = (): string => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '@#$%&*';
    const all = uppercase + lowercase + numbers + special;

    let password = '';
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];

    for (let i = 4; i < 12; i++) {
      password += all[Math.floor(Math.random() * all.length)];
    }

    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleResetPassword = (userId: string, userName: string) => {
    const newPassword = generateRandomPassword();
    setResetPassword({
      isOpen: true,
      userId,
      userName,
      newPassword
    });
  };

  const confirmResetPassword = () => {
    if (resetPassword.userId && resetPassword.newPassword) {
      resetPasswordMutation.mutate({
        userId: resetPassword.userId,
        password: resetPassword.newPassword
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('success', 'Copied', 'Password copied to clipboard');
  };

  const handleAddNew = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const getRolePermissions = (role: string): string[] => {
    return ROLE_PERMISSIONS[role] || [];
  };

  const countEnabledPermissions = (permissions: Record<string, boolean>): number => {
    return Object.values(permissions).filter(enabled => enabled === true).length;
  };

  const handleStartEdit = (user: any) => {
    setEditingId(user._id);
    setEditFormData({
      userId: user._id,
      role: user.role,
      customPermissions: user.customPermissions || {},
    });
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    // Keep customPermissions as Record<string, boolean> (only enabled ones)
    const enabledPermissions = Object.entries(editFormData.customPermissions)
      .filter(([_, enabled]) => enabled)
      .reduce((acc, [permission]) => {
        acc[permission] = true;
        return acc;
      }, {} as Record<string, boolean>);

    updateMutation.mutate({
      ...editFormData,
      customPermissions: enabledPermissions
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({
      userId: '',
      role: 'admin',
      customPermissions: {},
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleExport = () => {
    const csvData = filteredUsers.map((user: any) => ({
      'User ID': user._id,
      'Name': user.name,
      'Email': user.email,
      'Role': roles.find(r => r.name.toLowerCase().replace(/\s+/g, '_') === user.role)?.name || user.role,
      'Status': user.isActive ? 'Active' : 'Inactive',
      'Last Login': user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never',
      'Created At': user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '',
    }));

    const csvContent = [
      Object.keys(csvData[0] || {}).join(','),
      ...csvData.map((row: any) => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredUsers = users?.filter((user: any) => {
    const matchesSearch = (user.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                          (user.email?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    const userStatus = user.isActive ? 'active' : 'inactive';
    const matchesStatus = statusFilter === 'all' || userStatus === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  const stats = {
    total: users?.length || 0,
    active: users?.filter((u: any) => u.isActive === true).length || 0,
    inactive: users?.filter((u: any) => u.isActive === false).length || 0,
    suspended: 0, // Not used in new schema
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/dashboard" className="flex items-center hover:text-blue-600 transition-colors">
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-400">Settings</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-semibold">User Management</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage system users and assign roles
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={handleExport} className="btn-secondary flex items-center text-sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button
            onClick={() => setIsAddingNew(true)}
            className="btn-primary flex items-center text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <UsersIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Active</p>
              <p className="text-3xl font-bold text-green-600">{stats.active}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Inactive</p>
              <p className="text-3xl font-bold text-gray-600">{stats.inactive}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl">
              <UserX className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Suspended</p>
              <p className="text-3xl font-bold text-red-600">{stats.suspended}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-xl">
              <UserX className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all min-w-[180px]"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Add New User Form */}
      {isAddingNew && (
        <div className="bg-white rounded-xl border-2 border-primary-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 px-6 py-4 border-b border-primary-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Plus className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Add New User</h2>
              </div>
              <button
                onClick={() => {
                  setIsAddingNew(false);
                  setFormData({
                    name: '',
                    email: '',
                    password: '',
                    role: 'admin',
                    customPermissions: {},
                    organization: '',
                    exams: [],
                  });
                }}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          <form onSubmit={handleAddNew} className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  placeholder="João Silva"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  placeholder="joao@fkpartners.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-12 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    placeholder="Min. 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Must be 8+ chars with uppercase, lowercase, and number
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                >
                  {roles.map((role) => (
                    <option key={role._id} value={role.name.toLowerCase().replace(/\s+/g, '_')}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Role Permissions Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">
                Base Permissions from {roles.find(r => r.name.toLowerCase().replace(/\s+/g, '_') === formData.role)?.name || formData.role}:
              </h4>
              <p className="text-sm text-blue-700">
                {getRolePermissions(formData.role).length} permissions included
              </p>
            </div>

            {/* Custom Permissions */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Additional Custom Permissions 
              </h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                <PermissionSelector
                  selectedPermissions={formData.customPermissions}
                  onChange={(permissions) => setFormData({ ...formData, customPermissions: permissions })}
                  rolePermissions={getRolePermissions(formData.role)}
                />
              </div>
            </div>

            {/* Permission Summary */}
            {countEnabledPermissions(formData.customPermissions) > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>Total permissions:</strong> {getRolePermissions(formData.role).length} base + {countEnabledPermissions(formData.customPermissions)} custom
                </p>
              </div>
            )}

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setIsAddingNew(false);
                  setFormData({
                    name: '',
                    email: '',
                    password: '',
                    role: 'admin',
                    customPermissions: {},
                    organization: '',
                    exams: [],
                  });
                }}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 mr-2" />
                {createMutation.isPending ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users Grid */}
      <div className="grid grid-cols-1 gap-4">
        {filteredUsers.map((user: any) => (
          <div key={user._id} className="bg-white rounded-xl border border-gray-200 hover:shadow-md transition-all overflow-hidden">
            {editingId === user._id ? (
              // Edit Mode
              <form onSubmit={handleUpdate}>
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 border-b border-amber-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        <Edit className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Edit User Permissions</h3>
                        <p className="text-sm text-gray-600">{user.name} ({user.email})</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Role Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Role
                    </label>
                    <select
                      value={editFormData.role}
                      onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {roles.map((role) => (
                        <option key={role._id} value={role.name.toLowerCase().replace(/\s+/g, '_')}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Base Permissions Summary */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">
                      Base Permissions from {roles.find(r => r.name.toLowerCase().replace(/\s+/g, '_') === editFormData.role)?.name || editFormData.role}:
                    </h4>
                    <p className="text-sm text-blue-700">
                      {getRolePermissions(editFormData.role).length} permissions included
                    </p>
                  </div>

                  {/* Custom Permissions */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Additional Custom Permissions
                    </h3>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                      <PermissionSelector
                        selectedPermissions={editFormData.customPermissions}
                        onChange={(permissions) => setEditFormData({ ...editFormData, customPermissions: permissions })}
                        rolePermissions={getRolePermissions(editFormData.role)}
                      />
                    </div>
                  </div>

                  {/* Permission Summary */}
                  {countEnabledPermissions(editFormData.customPermissions) > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-800">
                        <strong>Total permissions:</strong> {getRolePermissions(editFormData.role).length} base + {countEnabledPermissions(editFormData.customPermissions)} custom
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updateMutation.isPending}
                      className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {updateMutation.isPending ? 'Updating...' : 'Update Permissions'}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              // View Mode
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Avatar */}
                    <div className="w-14 h-14 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-lg font-bold text-white">
                        {getInitials(user.name || 'User')}
                      </span>
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-base font-semibold text-gray-900">{user.name}</h3>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          user.isActive ? 'bg-green-100 text-green-700 border border-green-200' :
                          'bg-gray-100 text-gray-700 border border-gray-200'
                        }`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-6 text-sm">
                        <div className="flex items-center text-gray-600">
                          <Mail className="w-4 h-4 mr-2 text-gray-400" />
                          <span>{user.email}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Shield className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium border border-blue-100">
                            {roles.find(r => r.name.toLowerCase().replace(/\s+/g, '_') === user.role)?.name || user.role}
                          </span>
                        </div>
                        {user.customPermissions && countEnabledPermissions(user.customPermissions) > 0 && (
                          <div className="flex items-center text-gray-600">
                            <span className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium border border-purple-100">
                              +{countEnabledPermissions(user.customPermissions)} custom
                            </span>
                          </div>
                        )}
                        <div className="flex items-center text-gray-500 text-xs">
                          <Calendar className="w-4 h-4 mr-1.5" />
                          Last login: {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 ml-6">
                    {/* Status Toggle */}
                    <button
                      onClick={() => toggleStatusMutation.mutate({ userId: user._id, isActive: !user.isActive })}
                      disabled={toggleStatusMutation.isPending}
                      className="flex items-center space-x-1.5 px-3 py-2 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: user.isActive ? '#dcfce7' : '#fee2e2',
                        borderColor: user.isActive ? '#86efac' : '#fecaca'
                      }}
                      title={user.isActive ? 'Click to deactivate' : 'Click to activate'}
                    >
                      <div className={`relative w-8 h-4 rounded-full transition-colors ${
                        user.isActive ? 'bg-green-500' : 'bg-red-500'
                      }`}>
                        <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                          user.isActive ? 'translate-x-4' : 'translate-x-0'
                        }`} />
                      </div>
                    </button>

                    {canEdit && (
                      <button
                        onClick={() => handleStartEdit(user)}
                        className="p-2.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-transparent hover:border-primary-200"
                        title="Edit user permissions"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                    )}
                    {canEdit && (
                      <button
                        onClick={() => handleResetPassword(user._id, user.name)}
                        className="p-2.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors border border-transparent hover:border-amber-200"
                        title="Reset password"
                      >
                        <Key className="w-5 h-5" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDeleteUser(user._id, user.name)}
                        className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                        title="Delete user"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <UsersIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No users found</h3>
          <p className="text-sm text-gray-500 mb-6">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your filters to see more results'
              : 'Get started by adding your first user'}
          </p>
          {!isAddingNew && (searchQuery || statusFilter !== 'all') === false && (
            <button
              onClick={() => setIsAddingNew(true)}
              className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors inline-flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First User
            </button>
          )}
        </div>
      )}


      {/* Reset Password Dialog */}
      {resetPassword.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Key className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Reset Password</h3>
                  <p className="text-sm text-white/90">{resetPassword.userName}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 mb-3">
                  A new temporary password has been generated. Copy it and share with the user.
                </p>
                <div className="relative">
                  <input
                    type="text"
                    value={resetPassword.newPassword}
                    readOnly
                    className="w-full px-4 py-3 pr-12 bg-white border border-amber-300 rounded-lg font-mono text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(resetPassword.newPassword || '')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                    title="Copy password"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  ⚠️ <strong>Important:</strong> This password will be hashed after confirmation. Make sure to copy it before closing this dialog.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  onClick={() => setResetPassword({ isOpen: false })}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    confirmResetPassword();
                    setResetPassword({ isOpen: false });
                  }}
                  disabled={resetPasswordMutation.isPending}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-amber-600 rounded-xl hover:bg-amber-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Key className="w-4 h-4 mr-2" />
                  {resetPasswordMutation.isPending ? 'Resetting...' : 'Confirm Reset'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
